<template>
    <div v-if="result !== null" class="statistics-result">
        <div class="statistics-result-toolbar">
            <div class="statistics-result-summary">
                <span v-if="result.summary">{{ result.summary.label }}: <b>{{ result.summary.value }}</b></span>
            </div>
            <div class="statistics-result-exports">
                <NevsButton @click="$emit('export', 'excel')"><i class="fa-solid fa-file-excel"></i> Excel</NevsButton>
                <NevsButton v-if="showInvoicesExcel" @click="$emit('export', 'invoices-excel')">
                    <i class="fa-solid fa-file-invoice"></i> {{ $LANG.Get('buttons.invoicesExcel') }}
                </NevsButton>
                <NevsButton @click="$emit('export', 'pdf')"><i class="fa-solid fa-file-pdf"></i> PDF</NevsButton>
            </div>
        </div>

        <NevsChart v-if="result.chart && result.chart.labels.length > 0"
                   :title="result.chart.title"
                   :labels="result.chart.labels"
                   :values="result.chart.values"
                   :suffix="result.chart.suffix"
                   :empty-text="emptyText"
                   class="statistics-result-chart"></NevsChart>

        <NevsSimpleGrid :fields="gridFields" :records="gridRecords" :clickable="false"
                        :row-class="rowClass" :empty-text="emptyText"></NevsSimpleGrid>

        <div v-if="result.records.length > visibleCount" class="statistics-result-more">
            <NevsButton @click="loadMore"><i class="fa-solid fa-chevron-down"></i> {{ $LANG.Get('buttons.loadMore') }}</NevsButton>
            <span class="statistics-result-count">{{ visibleCount }} / {{ result.records.length }}</span>
        </div>
    </div>
</template>

<script>
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsSimpleGrid from "@/components/nevs/NevsSimpleGrid.vue";
import NevsChart from "@/components/nevs/NevsChart.vue";

export default {
    name: "StatisticsResult",
    components: {NevsChart, NevsSimpleGrid, NevsButton},
    props: {
        // The generic report shape: { columns, records, totals, chart, summary } or null when nothing has
        // been loaded. `totals`, when the report provides one, becomes the grid's summary row.
        result: {
            type: Object,
            default: null
        },
        emptyText: {
            type: String,
            default: ''
        },
        pageSize: {
            type: Number,
            default: 15
        },
        showInvoicesExcel: {
            type: Boolean,
            default: false
        }
    },
    emits: ['export'],
    data() {
        return {
            visibleCount: this.pageSize
        }
    },
    computed: {
        gridFields() {
            if (this.result === null) return [];
            return this.result.columns.map((column) => ({
                name: column.key,
                label: column.label,
                align: column.align
            }));
        },
        visibleRecords() {
            if (this.result === null) return [];
            return this.result.records.slice(0, this.visibleCount);
        },
        // The records the grid renders: the visible page, followed by the report's summary row when it has one.
        // The summary is a record like any other (its keys are the column keys), marked so rowClass can pick it
        // out and style it as the totals line. It sums the whole report, not just the rows currently shown.
        gridRecords() {
            if (this.result === null) return [];
            if (!this.result.totals) return this.visibleRecords;
            return this.visibleRecords.concat([Object.assign({}, this.result.totals, {is_totals_row: true})]);
        }
    },
    watch: {
        result() {
            this.visibleCount = this.pageSize;
        }
    },
    methods: {
        rowClass(record) {
            return record.is_totals_row ? 'statistics-result-totals' : null;
        },
        loadMore() {
            this.visibleCount += this.pageSize;
        }
    }
}
</script>

<style scoped>
.statistics-result {
    margin-top: 15px;
    min-width: 0;
}

.statistics-result-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 10px;
}

.statistics-result-summary {
    font-size: 15px;
}

.statistics-result-exports {
    display: flex;
    gap: 8px;
}

.statistics-result-chart {
    margin: 10px 0 20px;
    padding: 15px;
    border: 1px solid #eceff4;
    border-radius: 8px;
    background: #fff;
    box-sizing: border-box;
    max-width: 100%;
    /* Clip anything wide; the chart provides its own horizontal scroll internally. */
    overflow: hidden;
}

/* The summary row travels through the grid as an ordinary record, so it is styled from here. */
:deep(.statistics-result-totals td) {
    font-weight: bold;
    border-top: 2px solid #ddd;
    border-bottom: none;
}

.statistics-result-more {
    margin-top: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
}

.statistics-result-count {
    color: #8a94a6;
    font-size: 13px;
}
</style>
